import { FormEventHandler } from "react";
import { Box, Typography } from "@mui/material";
import { useForm } from "react-hook-form";

import * as yup from "yup";

import api from "../services/api";
import useAppTranslations from "../hooks/useAppTranslations";
import Landing from "../components/Landing";
import { IPasswordRegisterForm, IRegisterValidationResponse } from "../types/forms";
import useYupFormResolver from "../hooks/useYupFormResolver";
import FormTextField from "../components/FormTextField";
import Translate from "../components/Translate";
import HashValidationWrapper from "../components/HashValidationWrapper";
import regex from "../helpers/regex";
import FormTextFieldPassword from "../components/FormTextFieldPassword";

const initialFormState: IPasswordRegisterForm = {
  id: "",
  userEmail: "",
  userName: "",
  companyName: "",
  password: "",
  confirmPassword: ""
};

export default function PasswordRegisterPage() {
  const { t } = useAppTranslations();

  const yupFormValidation = yup.object({
    password: yup
      .string()
      .required(
        t("validation:required_field", {
          fieldName: t("form:field:password")
        })
      )
      .min(8, t("validation:password_min_chars", { count: 8 }))
      .matches(regex.INCLUDE_NUMBER_CHAR, t("validation:password_number"))
      .matches(regex.INCLUDE_UPPER_CHAR, t("validation:password_min_uppercase_letter"))
      .matches(regex.INCLUDE_SPECIAL_CHAR, t("validation:password_reserved_char")),

    confirmPassword: yup
      .string()
      .required(
        t("validation:required_field", {
          fieldName: t("form:field:password_confirm")
        })
      )
      .oneOf([yup.ref("password")], t("validation:valid_confirm_password"))
  });

  const yupFormResolver = useYupFormResolver<typeof yupFormValidation>(yupFormValidation);

  const formHook = useForm<IPasswordRegisterForm>({
    mode: "onSubmit",
    resolver: yupFormResolver,
    values: initialFormState
  });

  const handleFormSubmit: FormEventHandler<HTMLFormElement> = ev => {
    ev.preventDefault();
    formHook.handleSubmit(data => {
      api.postRegisterPassword(data).then(v => {
        if (v.data.success) {
          window.location.replace(process.env.REACT_APP_BIZPIK_FRONT_URL as string);
        }
      });
    })(ev);
  };

  const handleRegisterValidation = (response: IRegisterValidationResponse) => {
    formHook.setValue("id", response.id);
    formHook.setValue("userEmail", response.userEmail);
    formHook.setValue("userName", response.userName);
    formHook.setValue("companyName", response.companyName);
  };

  const companyName = formHook.watch("companyName");

  return (
    <HashValidationWrapper onValidateHash={handleRegisterValidation}>
      <Landing
        headerTitle={t("title:create_new_password")}
        submitButtonLabel={t("action:create_new_password")}
        handleCardFormSubmit={handleFormSubmit}
        CardFormContent={
          <>
            {companyName && (
              <Box className="mb-6">
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: theme => theme.palette.text.primary
                  }}
                >
                  {companyName}
                </Typography>
              </Box>
            )}

            <Box
              className="rounded-2xl"
              sx={theme => ({
                py: 3,
                px: 4,
                background: theme.palette.background.default,
                border: `solid 1px ${theme.palette.grey[300]}`,

                [theme.breakpoints.down("sm")]: {
                  border: "none",
                  py: 1.5,
                  px: 2
                }
              })}
            >
              <Box className="grow">
                <Typography variant="subtitle2">
                  <Translate src="form:legend:access_form" />
                </Typography>

                <FormTextField
                  id="userName"
                  label={t("form:field:full_name")}
                  placeholder={t("form:placeholder:full_name")}
                  fieldControl={formHook.control}
                  fieldName="userName"
                  disabled
                />

                <FormTextFieldPassword
                  id="password"
                  label={t("form:field:password")}
                  placeholder={t("form:placeholder:password")}
                  fieldControl={formHook.control}
                  fieldName="password"
                  fieldType="password"
                />

                <FormTextFieldPassword
                  id="confirmPassword"
                  label={t("form:field:password_confirm")}
                  placeholder={t("form:placeholder:password_confirm")}
                  fieldType="password"
                  fieldControl={formHook.control}
                  fieldName="confirmPassword"
                  gutters={false}
                />
              </Box>
            </Box>
          </>
        }
      />
    </HashValidationWrapper>
  );
}
